import React, { ReactNode, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../firebase/interfaces';
import {
  AUTH_MFA_SETUP,
  AUTH_SIGN_UP_PROVIDER,
  DOWNLOAD_BLOCK_ACCOUNT_DATA,
  DOWNLOAD_MY_DATA,
  LOGOUT,
} from 'utilities/routes';
import { FullPageLoader } from 'components/loaderComponent';
import { useMFA } from '../firebase/hooks';
import { ADMIN_ROLES } from '../utilities/constants';

const toBase64 = (str: string): string => {
  try {
    return encodeURIComponent(window.btoa(unescape(encodeURIComponent(str))));
  } catch {
    return encodeURIComponent(str);
  }
};

interface AuthenticatedRouteProps {
  children?: ReactNode;
}

const Loader = () => {
  useEffect(() => {
    const reloadTimer = setTimeout(() => {
      window.location.reload();
    }, 5_000);
    return () => clearTimeout(reloadTimer);
  }, []);
  return <FullPageLoader />;
};

export const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
}) => {
  const location = useLocation();
  const { isLoggedIn, isAuthResolved, user, loadingUserFullInfo } = useAuth();
  const { getEnrolledFactors } = useMFA();
  const isDebug = Boolean(process.env.REACT_APP_IS_DEBUG_MODE);

  if (!isLoggedIn) {
    if (location.pathname === LOGOUT) {
      return null;
    }
    return <Navigate to={LOGOUT} state={{ from: location }} replace />;
  }

  if (!isAuthResolved || !user?.role) {
    return <Loader />;
  }

  if (user?.blocked_at) {
    if (location.pathname === DOWNLOAD_BLOCK_ACCOUNT_DATA) return null;
    return <Navigate to={DOWNLOAD_BLOCK_ACCOUNT_DATA} replace />;
  }
  if (user?.account_closed_at) {
    if (location.pathname === DOWNLOAD_MY_DATA) return null;
    return <Navigate to={DOWNLOAD_MY_DATA} replace />;
  }

  const isPatient = user?.role === Roles.PATIENT;
  const isAdmin = ADMIN_ROLES.includes(user?.role as Roles);
  const enrolledFactors = getEnrolledFactors();
  const isMfaEnrolled = Boolean(enrolledFactors && enrolledFactors.length > 0);

  const requiresMFA = Boolean(!isDebug && (isPatient || isAdmin));

  if (requiresMFA && !isMfaEnrolled) {
    const redirectUrl = `${location.pathname ?? ''}${location.search ?? ''}`;
    const redirectParam = redirectUrl
      ? `?redirect=${toBase64(redirectUrl)}`
      : '';
    return <Navigate to={`${AUTH_MFA_SETUP}${redirectParam}`} replace />;
  }

  if (
    user?.role === Roles.PROVIDER &&
    !user.displayName &&
    !loadingUserFullInfo
  ) {
    if (location.pathname === AUTH_SIGN_UP_PROVIDER) return null;
    return <Navigate to={AUTH_SIGN_UP_PROVIDER} replace />;
  }

  return <>{children ?? <Outlet />}</>;
};

export default AuthenticatedRoute;
